import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { logger } from '../../lib/logger';

/**
 * Hook for ICD-10 typeahead search.
 * Encapsulates query state, debounced search, results, and outside-click close.
 *
 * Returns: { wrapperRef, query, setQuery, results, searching, open, setOpen,
 *            handleInputChange, handleSelect, handleClear }
 */
export const useIcd10Search = ({ onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get('/lookup/icd10', { params: { query: term } });
      setResults(res.data.results || []);
      setOpen(true);
    } catch (e) {
      logger.warn('[ICD10Search] lookup failed', e);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (code) => {
    onChange(code.code, code.description, code.billable);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('', '', false);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return {
    wrapperRef,
    query,
    results,
    searching,
    open,
    handleInputChange,
    handleSelect,
    handleClear,
  };
};
